import { extend } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';

/**
 * PKG-FORO-HEADER-OVERFLOW — publica el alto REAL del header en `--cm-header-h`.
 *
 * EL PORQUE ENTERO ESTA EN `less/forum.less` §3d-bis, con las medidas. En corto: entre 768 y
 * 1309.98px el header se parte en dos filas (y en tres por debajo de ~790px, donde el nav no
 * cabe al lado del titulo), pero el core lo dimensiona y reserva su hueco con
 * `--header-height`, que sigue valiendo 52. Todo lo anclado al token queda corto y la fila de
 * sesion se queda sin fondo, que es lo que hacia que «Regístrate» y el CTA de `/n8n-chile` se
 * pisaran al hacer scroll.
 *
 * ESTE MODULO NO DECIDE NADA DE DISENO: solo mide. Quien decide es la hoja.
 *   · La FRANJA la declara el LESS y se lee aqui como `--cm-header-band`. Asi el 768/1309.98
 *     vive en un solo sitio; si manana la franja cambia, este fichero no se toca.
 *   · Fuera de la franja se BORRA la propiedad en vez de dejar el ultimo valor medido: por
 *     debajo de 768px el header del movil se mide distinto (el drawer cuenta dentro) y un
 *     numero heredado de otro ancho seria una trampa para quien lo lea despues.
 *
 * POR QUE UN `ResizeObserver` Y NO UN `resize` DE VENTANA: el alto del header cambia tambien
 * sin que cambie la ventana —al abrir sesion (la fila de sesion pasa de dos botones a
 * avatar+campana), al terminar de cargar las fuentes de Google (las etiquetas del nav cambian
 * de ancho y con ellas el punto en que el nav cae a su propia linea)—, y el observador coge
 * los tres casos con una sola suscripcion.
 */

/**
 * Aire bajo la ultima fila del header. NO es un numero elegido: es el que el core deja a
 * >=1310px, donde el header cabe en una fila y el token ya es correcto — caja 52, contenido
 * hasta 45. Reproducirlo aqui hace que la formula devuelva exactamente 52 en ese tramo, que es
 * la comprobacion de que mide lo que dice medir.
 */
const AIR = 7;

/** Ultimo valor escrito, para no tocar el DOM en cada pasada del observador. */
let published: string | null = null;

function measure(container: HTMLElement) {
  const header = container.closest('.App-header');

  if (!header) return;

  const root = document.documentElement;
  const inBand = getComputedStyle(root).getPropertyValue('--cm-header-band').trim() === '1';

  if (!inBand) {
    if (published !== null) {
      root.style.removeProperty('--cm-header-h');
      published = null;
    }

    return;
  }

  // Diferencia entre cajas y no `offsetTop`: el `offsetParent` del contenedor depende de que
  // el header este posicionado, y eso cambia con el breakpoint (§3c lo fija en escritorio y
  // deja el del movil al core).
  const value =
    Math.round(container.getBoundingClientRect().bottom - header.getBoundingClientRect().top + AIR) + 'px';

  if (value === published) return;

  published = value;
  root.style.setProperty('--cm-header-h', value);
}

export default function headerHeight() {
  // `oncreate` de HeaderSecondary y no `app.mount()`: es el punto en que el header ya esta en
  // el DOM, y es ademas el componente que cambia de contenido al abrir o cerrar sesion.
  extend(HeaderSecondary.prototype, 'oncreate', function () {
    const container = document.querySelector<HTMLElement>('.App-header .container');

    if (!container || container.dataset.cmHeaderObserved === '1') return;

    container.dataset.cmHeaderObserved = '1';

    // El observador dispara ya en el `observe()` inicial, asi que no hace falta una primera
    // medida a mano.
    new ResizeObserver(() => measure(container)).observe(container);
  });
}
